import { HistoryOutlined } from '@ant-design/icons';
import {
	VaunceAppPKGTicketOnPosType,
	VaunceAppTicketOnPosType,
} from '@kinderlabs-pos/shared-data-type';
import { getUuidV4 } from '@kinderlabs-pos/shared-util';
import { OrderState, StoreInfoState } from '@kinderlabs-pos/state';
import { WithDialog } from '@kinderlabs-pos/ui-atoms';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { VaunceAppTicketHistoryDialog } from './VaunceAppTicketHistoryDialog';
import { VaunceAppTicketUserMemberInfoBox } from './common/VaunceAppTicketUserMemberInfoBox';
import { VaunceTicketItemAccordion } from './common/VaunceTicketItemAccordion';
import { getSortedUserIdList } from './common/getSortedUserIdList';

export const VaunceAppTicketPkgListContent = ({
	appTicketInfoMap,
}: {
	appTicketInfoMap: {
		[userId: string]: (VaunceAppPKGTicketOnPosType & { canUseTicket: boolean })[];
	};
}) => {
	const userIdList = getSortedUserIdList({ ticketOrCouponInfoMap: appTicketInfoMap });

	return (
		<Stack>
			{userIdList.map((memberId, idx) => {
				const { username, telephone } = appTicketInfoMap[memberId][0];

				return (
					<Stack
						key={idx}
						spacing={1}>
						<VaunceAppTicketUserMemberInfoBox
							memberId={memberId}
							userName={username}
							userTelephone={telephone}
						/>
						{appTicketInfoMap[memberId].map((pkg, pkgIdx) => {
							return (
								<VaunceTicketItemAccordion
									key={pkgIdx}
									ticket={pkg}
									canUseTicket={pkg.canUseTicket && !pkg.isExpired}
									rightPanel={
										<Stack
											width={'33%'}
											direction={'row'}
											justifyContent={'flex-end'}
											spacing={1}>
											<VaunceAppPkgUseButton pkg={pkg} />
											<WithDialog
												dialog={(open, closeDialog) => (
													<VaunceAppTicketHistoryDialog
														ticketId={pkg.ticketId}
														ticketType={'ONE_TIME'}
														open={open}
														closeDialog={closeDialog}
													/>
												)}>
												{(openDialog) => (
													<IconButton onClick={openDialog}>
														<HistoryOutlined />
													</IconButton>
												)}
											</WithDialog>
										</Stack>
									}>
									<></>
								</VaunceTicketItemAccordion>
							);
						})}
					</Stack>
				);
			})}
		</Stack>
	);
};

const VaunceAppPkgUseButton = ({ pkg }: { pkg: VaunceAppPKGTicketOnPosType }) => {
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);
	const ticketUsages = useAtomValue(OrderState.cart.lines.ticketUsageSelector).filter(
		(usage) => usage.type === 'VAUNCE_APP_PKG'
	);
	const cartDispatch = OrderState.cart.useDispatcher();

	const status =
		pkg.ticketStatus !== 'USABLE'
			? '사용불가'
			: ticketUsages.some((u) => u.ticketUsageInfo.ticketUsageId === `${pkg.ticketId}`)
			? '사용중'
			: ('사용가능' as const);

	const handleClickButton = () => {
		if (status !== '사용중') {
			cartDispatch({
				type: 'USAGES',
				subAction: {
					type: 'ADD_TICKET',
					cartLineId: getUuidV4(),
					ticketUsage: {
						type: 'PACKAGE',
						useId: `${pkg.ticketId}`,
						name: pkg.ticketTitle,
						ticketId: pkg.ticketId,
						ticketInfoId: 0,
						userName: pkg.username,
						userTelephone: pkg.telephone,
						memberId: pkg.memberId,
						ddiziInfo: pkg.pkgItems
							.filter((item) => item.ticketType === 'ONE_TIME')
							.map((item) => ({
								name: item.name,
								ddiziType: 'NORMAL',
								//@ts-ignore
								minutes: item.ticketMinutes ?? storeInfo.policy.defaultMinutesInCenter,
								hasNoAdditionalCharge: false,
								available: 'ALL',
								isNotForStatistics: false,
							})),
						// ddiziInfo: pkg.pkgItems.map((item) => ({
						// 	name: item,
						// 	ddiziType: 'NORMAL',
						// 	minutes: storeInfo.policy.defaultMinutesInCenter,
						// 	hasNoAdditionalCharge: false,
						// 	isNotForStatistics: false,
						// 	available: 'ALL',
						// })),
					},
				},
			});
		} else {
			cartDispatch({
				type: 'USAGES',
				subAction: { type: 'DELETE', useId: `${pkg.ticketId}`, ticketType: 'PACKAGE' },
			});
		}
	};

	return (
		<Button
			variant='outlined'
			sx={{ flex: 1 }}
			disabled={pkg.ticketStatus !== 'USABLE'}
			color={pkg.ticketStatus === 'USABLE' ? 'warning' : 'error'}
			onClick={handleClickButton}>
			<Typography variant='body1'>
				{status === '사용불가' ? '사용불가' : status === '사용가능' ? '사용' : '사용 취소'}
			</Typography>
		</Button>
	);
};
